import { useState, useEffect, useCallback, useRef } from 'react';
import { getBlogImageUploadUrl, uploadFileToS3 } from '../../../../services/blogService';
import {
    BLOG_ALLOWED_IMAGE_TYPES,
    BLOG_MAX_IMAGE_SIZE_MB,
} from '../../../../constants/api.constants';

interface BlogImageUploaderProps {
    onUploadComplete: (result: { blogId: string; cloudFrontUrl: string }) => void;
    currentImageUrl?: string;
    disabled?: boolean;
}

const ACCEPTED_TYPES = BLOG_ALLOWED_IMAGE_TYPES.join(',');
const MAX_BYTES = BLOG_MAX_IMAGE_SIZE_MB * 1024 * 1024;

export default function BlogImageUploader({
    onUploadComplete,
    currentImageUrl,
    disabled = false,
}: BlogImageUploaderProps) {
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl ?? null);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync preview when the parent provides an imageUrl after async load (edit mode)
    useEffect(() => {
        if (currentImageUrl && !previewUrl) {
            setPreviewUrl(currentImageUrl);
        }
    }, [currentImageUrl, previewUrl]);

    const handleUpload = useCallback(
        async (file: File) => {
            setError(null);

            const allowedTypes: readonly string[] = BLOG_ALLOWED_IMAGE_TYPES;
            if (!allowedTypes.includes(file.type)) {
                setError('Only JPG, PNG, and WEBP images are allowed.');
                return;
            }
            if (file.size > MAX_BYTES) {
                setError(`Image must be smaller than ${BLOG_MAX_IMAGE_SIZE_MB}MB.`);
                return;
            }

            setUploading(true);
            try {
                const fileType = file.type as 'image/jpeg' | 'image/png' | 'image/webp';
                const { blogId, uploadUrl, cloudFrontUrl } = await getBlogImageUploadUrl({
                    fileType,
                });
                await uploadFileToS3(uploadUrl, file, fileType);
                setPreviewUrl(cloudFrontUrl);
                onUploadComplete({ blogId, cloudFrontUrl });
            } catch {
                setError('Upload failed. Please try again.');
            } finally {
                setUploading(false);
            }
        },
        [onUploadComplete]
    );

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                handleUpload(file);
            }
        },
        [handleUpload]
    );

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragging(false);
            if (disabled || uploading) return;
            const file = e.dataTransfer.files?.[0];
            if (file) {
                handleUpload(file);
            }
        },
        [disabled, uploading, handleUpload]
    );

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleClick = useCallback(() => {
        if (!disabled && !uploading) {
            inputRef.current?.click();
        }
    }, [disabled, uploading]);

    return (
        <div className="space-y-2">
            <div
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`relative border-2 border-dashed rounded-xl transition-all duration-200 overflow-hidden min-h-[200px] ${
                    isDragging
                        ? 'border-[#6e64f0] bg-[#f1f0ff]'
                        : 'border-[#e2e8f0] hover:border-[#6e64f0] bg-[#f8fafc]'
                } ${disabled || uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                {previewUrl ? (
                    <div className="relative w-full h-full">
                        <img
                            src={previewUrl}
                            alt="Blog cover"
                            className="w-full h-64 object-cover"
                        />
                        {!disabled && (
                            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="bg-white/90 rounded-lg px-4 py-2 flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4 text-[#6e64f0]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium text-[#1e293b]">
                                        Change image
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                        {uploading ? (
                            <div className="flex flex-col items-center gap-3">
                                <div className="w-10 h-10 border-2 border-[#6e64f0] border-t-transparent rounded-full animate-spin" />
                                <p className="text-sm text-[#64748b] font-medium">Uploading...</p>
                            </div>
                        ) : (
                            <>
                                <div className="w-12 h-12 rounded-xl bg-[#ede9fe] flex items-center justify-center mb-4">
                                    <svg
                                        className="w-6 h-6 text-[#6e64f0]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                        />
                                    </svg>
                                </div>
                                <p className="text-base font-semibold text-[#1e293b] mb-1">
                                    Upload cover image
                                </p>
                                <p className="text-sm text-[#64748b]">
                                    Drag and drop or click to browse
                                </p>
                                <p className="text-xs text-[#94a3b8] mt-2">
                                    JPG, PNG, WEBP · Max {BLOG_MAX_IMAGE_SIZE_MB}MB
                                </p>
                            </>
                        )}
                    </div>
                )}

                {uploading && previewUrl && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                        <div className="w-10 h-10 border-2 border-[#6e64f0] border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
            </div>

            {error && (
                <p className="text-sm text-[#ef4444] flex items-center gap-1.5">
                    <svg
                        className="w-4 h-4 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    {error}
                </p>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                className="sr-only"
                onChange={handleFileChange}
                disabled={disabled || uploading}
            />
        </div>
    );
}
